import React, { useContext, useState, useRef } from 'react';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { useFormik } from 'formik';
import { postRequest } from '../../../api/ApiController';
import { refreshTrainingInvitationTemplates } from '../../../utils/trainingInvitationTemplatesLookup';
import { SaveIcon, AddIcon, CopyIcon, VerticalLineIcon, FavoriteIcon, PriceIcon, SaveAndCloseIcon, InviteDate, InviteTime, InviteLocation, TrainingTheme } from '../../svg/svgIcons';
import AuthContext from '../../../components/AuthContext';
import UtilContext from '../../../components/UtilContext';
import { LocaleContext } from '../../../components/LocaleContext';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import { InputTextarea } from 'primereact/inputtextarea';

const ADD_MASK = 'trng030';

function invitationTemplateFormSeed(template, type) {
  if (!template || type === 'add') {
    return {
      name: template?.name ?? '',
      description: template?.description ?? '',
      content: template?.content ?? '',
    };
  }
  return template;
}

function invitationTemplateRowFromResponse(response) {
  const row = response?.data;
  if (row && typeof row === 'object' && !Array.isArray(row) && row.id != null) return row;
  return null;
}

export default function TrainingInvitationTemplateAdd({ template, type = 'add' }) {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t } = useContext(LocaleContext);
  const { openFavoriteDialog, toast, setFastView, setVisible, setDialogText } = useContext(UtilContext);

  const [closeTab, setCloseTab] = useState(false);
  const submitRef = useRef(null);
  const contentRef = useRef(null);

  const [formTemplate, setFormTemplate] = useState(() => invitationTemplateFormSeed(template, type));
  const [formType, setFormType] = useState(type);

  const canAdd = user.permissions?.includes('add training-invitation');
  const canEdit = user.permissions?.includes('edit training-invitation');
  const saveDisabled = formType === 'add' ? !canAdd : !canEdit;

  function openAddTab(nextTemplate) {
    dispatch({
      type: 'create_new_tab',
      name: t('TrainingInvitationTemplateList.Tab.New'), translationKey: 'TrainingInvitationTemplateList.Tab.New', legacyLabel: t('TrainingInvitationTemplateList.Tab.New'),
      page: 'TrainingInvitationTemplateAdd',
      props: { template: nextTemplate, type: 'add' },
      mask: ADD_MASK,
    });
  }

  const formik = useFormik({
    initialValues: formTemplate == null ? { name: '', description: '', content: '' } : formTemplate,
    enableReinitialize: true,
    onSubmit: (values) => {
      setDialogText(t('Common.Saving'));
      setVisible(true);
      const formData = new FormData();
      formData.append('name', values.name ?? '');
      formData.append('description', values.description ?? '');
      formData.append('content', values.content ?? '');
      const url = formType === 'add' ? '/api/training_invitation_templates' : `/api/training_invitation_templates/update/${formik.values.id}`;
      postRequest(url, formData, true)
        .then((saveResponse) => {
          const savedRow = invitationTemplateRowFromResponse(saveResponse);
          return refreshTrainingInvitationTemplates({ dispatch }).then(() => {
            if (savedRow) {
              formik.setValues(savedRow);
              setFormTemplate(savedRow);
            }
            if (closeTab) dispatch({ type: 'close_tab', id: state.selectedTab });
            setFormType('edit');
            setVisible(false);
            toast?.current?.show({
              severity: 'info',
              summary: t('TrainingInvitationTemplateList.Toast.DeletedSummary'),
              life: 3000,
              detail: formType === 'add' ? t('TrainingInvitationTemplateAdd.Toast.Saved') : t('TrainingInvitationTemplateAdd.Toast.Updated'),
            });
          });
        })
        .catch(() => {
          setVisible(false);
          alert(t('TrainingInvitationTemplateAdd.Toast.Failed'));
        });
    },
  });

  function insertMyText(textToInsert) {
    const el = contentRef.current;
    if (!el) return;
    const cursorPosition = el.selectionStart ?? String(formik.values.content || '').length;
    const current = String(formik.values.content ?? '');
    const next = current.substring(0, cursorPosition) + textToInsert + current.substring(cursorPosition);
    formik.setFieldValue('content', next);
  }

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="saveandclose"><button className="disabled:opacity-25" disabled={saveDisabled} type="submit" onClick={() => { setCloseTab(true); submitRef.current.click(); }}><SaveAndCloseIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="save"><button className="disabled:opacity-25" disabled={saveDisabled} type="submit" onClick={() => submitRef.current.click()}><SaveIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="add"><button className="disabled:opacity-25" disabled={!canAdd} onClick={() => openAddTab(null)}><AddIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="copy"><button className="disabled:opacity-25" disabled={!canAdd} onClick={() => openAddTab({ name: formik.values.name, description: formik.values.description, content: formik.values.content })}><CopyIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite"><button onClick={() => openFavoriteDialog(t('TrainingInvitationTemplateList.Tab.New'), 'TrainingInvitationTemplateAdd')}><FavoriteIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="fast"><button onClick={() => setFastView('TrainingInvitationTemplateAdd', null, null)}><PriceIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
        </IntersectionObserverWrapper>
      </div>
      <form onSubmit={formik.handleSubmit}>
        <div className="relative flex flex-col overflow-x min-h-[calc(100vh-177px)]">
          <div className="xl:grid grid-cols-2 gap-1 mb-2 ">
            <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md">
              <h1 className="text-3xl font-semibold text-left text-zinc-900">
                {t('TrainingInvitationTemplateAdd.Title')}
              </h1>
              {formType === 'edit' && <label className="block text-md font-semibold text-gray-800">{t('TrainingInvitationTemplateAdd.Label.Id')}: {formik.values.id}</label>}
              <div>
                <label htmlFor="name" className="block text-md font-semibold text-gray-800">
                  {t('TrainingInvitationTemplateAdd.Label.Name')}
                </label>
                <input
                  name="name"
                  id="name"
                  type="text"
                  required
                  onChange={formik.handleChange}
                  value={formik.values.name}
                  className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                />
              </div>
              <div>
                <label htmlFor="description" className="block text-md font-semibold text-gray-800">
                  {t('TrainingInvitationTemplateAdd.Label.Description')}
                </label>
                <textarea
                  name="description"
                  id="description"
                  onChange={formik.handleChange}
                  value={formik.values.description}
                  className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                />
              </div>
              <div>
                <label htmlFor="content" className="block text-md font-semibold text-gray-800">
                  {t('TrainingInvitationTemplateAdd.Label.Content')}
                </label>
                <div className="w-full h-12 flex flex-row justify-start gap-1 px-1 py-1 text-white">
                  <div><button type="button" className="px-2 py-2 rounded-md bg-[#333] hover:bg-gray-600" onClick={() => insertMyText('[Dátum]')}><InviteDate /></button></div>
                  <div><button type="button" className="px-2 py-2 rounded-md bg-[#333] hover:bg-gray-600" onClick={() => insertMyText('[Čas]')}><InviteTime /></button></div>
                  <div><button type="button" className="px-2 py-2 rounded-md bg-[#333] hover:bg-gray-600" onClick={() => insertMyText('[Miesto]')}><InviteLocation /></button></div>
                  <div><button type="button" className="px-2 py-2 rounded-md bg-[#333] hover:bg-gray-600" onClick={() => insertMyText(t('Training.Placeholder.TrainingName'))}><TrainingTheme /></button></div>
                </div>
                <InputTextarea
                  autoResize
                  name="content"
                  id="content"
                  required
                  onChange={formik.handleChange}
                  value={formik.values.content}
                  rows={5}
                  cols={30}
                  ref={contentRef}
                  className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                />
              </div>
              <button type="submit" ref={submitRef} className="hidden" />
            </div>
          </div>
        </div>
      </form>
    </>
  );
}
